import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUIVersion, selectUserChoiceValues } from "store/app";
import { useThemeUI } from "theme-ui";
import flow from "lodash/fp/flow";

import { Button } from "components";
import { ButtonWithImageOptions, IconButtonOptions } from "components/Forms";
import { ChevronLeftIcon } from "components/icons";
import { shouldBeDisplayed } from "utils";
import { isArray } from "lodash/fp";

export default function GroupedIconButtonOptions({
  name,
  label,
  labelIcon,
  options,
  toolTip,
  defaultValue,
  value,
  onChange,
  isDisabled,
  isFieldTouched,
  setTouchedField,
  setFocusedField,
  goBackFocusedFields,
  defaultValueToSetOnRender,
  resetButtonOption,
  goBackOnSelect,
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const UIVersion = useSelector(selectUIVersion);
  const userChoiceValues = useSelector(selectUserChoiceValues);

  const [displayOptionsFor, setDisplayOptionsFor] = useState("");

  // UI V1 Mobile stacks vertically, V2 mobile mirrors desktop
  const UIV1MobileCheck = UIVersion === 1 && isMobile;

  const goBack = () => {
    setDisplayOptionsFor("");
    if (!UIV1MobileCheck) {
      goBackFocusedFields();
    }
  };

  const selectedOption = value?.selectedOption;

  useEffect(() => {
    if (displayOptionsFor !== "") {
      if (!UIV1MobileCheck) {
        setFocusedField();
      }
    }
  }, [displayOptionsFor]);

  useEffect(() => {
    if (!shouldBeDisplayed(userChoiceValues)(value)) return;
    if (isDisabled) {
      onChange(null);
      setDisplayOptionsFor("");
    } else if (!isDisabled && !selectedOption) {
      const valueDefaultValue = isArray(value?.defaultValue)
        ? value.defaultValue[0]
        : value?.defaultValue;
      onChange(valueDefaultValue || null);
    }
  }, [isDisabled, selectedOption]);

  // To distinguish between showing checkmark icon for
  // new goalie-gear but not existing products
  const parentShouldBeSelected =
    value?.parentLabelShouldShowIfItIsSelected &&
    value?.value &&
    isFieldTouched;

  // CSS in json will overwrite labelIcon
  labelIcon = selectedOption?.css && (labelIcon = selectedOption);

  return (
    <>
      {displayOptionsFor === "" && (
        <ButtonWithImageOptions
          options={[
            {
              label,
              value: label,
              iconAfter: selectedOption?.icon || labelIcon,
              toolTip: toolTip,
            },
          ]}
          onChange={flow(setDisplayOptionsFor, setTouchedField)}
          isDisabled={isDisabled}
          isSelected={parentShouldBeSelected}
        />
      )}

      {displayOptionsFor !== "" && (
        <IconButtonOptions
          name={name}
          options={options}
          defaultValue={defaultValue}
          value={value?.value}
          onChange={(v) => {
            if (v === value?.value || goBackOnSelect) {
              goBack();
            }
            onChange(v);
          }}
          defaultValueToSetOnRender={defaultValueToSetOnRender}
          backButton={
            <Button
              variant="secondary"
              size={UIVersion === 2 && isMobile ? "medium" : "large"}
              iconBefore={<ChevronLeftIcon />}
              aria-label="go back"
              isRound
              onClick={goBack}
            />
          }
          resetButtonOption={resetButtonOption}
        />
      )}
    </>
  );
}
